import { useState, useMemo } from "react";
import Layout from "@/components/feature/Layout";
import { musterilerData, type Musteri } from "@/mocks/musteriler";

function formatCurrency(value: number, currency = "₺"): string {
  return `${value.toLocaleString("tr-TR")} ${currency}`;
}

export default function Musteriler() {
  const [search, setSearch] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<"table" | "card">("table");

  const [data, setData] = useState<Musteri[]>(musterilerData);

  const filtered = useMemo(() => {
    return data.filter((item) => {
      const q = search.toLowerCase();
      return (
        item.adSoyad.toLowerCase().includes(q) ||
        item.telefon.includes(q) ||
        item.eposta.toLowerCase().includes(q) ||
        item.adres.toLowerCase().includes(q) ||
        item.not.toLowerCase().includes(q)
      );
    });
  }, [data, search]);

  const toplamMusteri = filtered.length;
  const toplamAlacak = filtered.reduce((s, i) => s + i.toplamAlacak, 0);
  const aktifMusteri = filtered.filter((i) => i.toplamAlacak > 0).length;

  const handleAdd = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const fd = new FormData(form);
    const yeni: Musteri = {
      id: Date.now(),
      adSoyad: String(fd.get("adSoyad") || ""),
      telefon: String(fd.get("telefon") || ""),
      eposta: String(fd.get("eposta") || ""),
      adres: String(fd.get("adres") || ""),
      not: String(fd.get("not") || ""),
      toplamAlacak: 0,
      toplamSiparis: 0,
      sonIslem: new Date().toISOString().split("T")[0],
    };
    setData((prev) => [yeni, ...prev]);
    setShowAddForm(false);
    form.reset();
  };

  const handleEdit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingId) return;
    const form = e.currentTarget as HTMLFormElement;
    const fd = new FormData(form);
    setData((prev) =>
      prev.map((item) =>
        item.id === editingId
          ? {
              ...item,
              adSoyad: String(fd.get("adSoyad") || ""),
              telefon: String(fd.get("telefon") || ""),
              eposta: String(fd.get("eposta") || ""),
              adres: String(fd.get("adres") || ""),
              not: String(fd.get("not") || ""),
            }
          : item
      )
    );
    setEditingId(null);
    form.reset();
  };

  const handleDelete = (id: number) => {
    setData((prev) => prev.filter((item) => item.id !== id));
  };

  const editRecord = data.find((d) => d.id === editingId);

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-100 mb-1">
          Müşteri Rehberi
        </h1>
        <p className="text-sm text-gray-500">
          Tüm müşterilerinizi tek yerde görün, yönetin ve yeni kayıtlar ekleyin.
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="glass-card p-4">
          <p className="text-xs text-gray-500 mb-1">Toplam Müşteri</p>
          <p className="text-xl font-semibold text-gray-100">{toplamMusteri} kişi</p>
        </div>
        <div className="glass-card p-4">
          <p className="text-xs text-gray-500 mb-1">Toplam Alacak</p>
          <p className="text-xl font-semibold text-gold-400">{formatCurrency(toplamAlacak)}</p>
        </div>
        <div className="glass-card p-4">
          <p className="text-xs text-gray-500 mb-1">Borcu Olan Müşteri</p>
          <p className="text-xl font-semibold text-red-400">{aktifMusteri} kişi</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
            <i className="ri-search-line text-gray-500 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Müşteri ara..."
              className="bg-transparent text-sm text-gray-300 placeholder-gray-500 outline-none w-48"
            />
          </div>
          <div className="flex items-center bg-white/5 border border-white/10 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode("table")}
              className={`px-3 py-2 text-sm cursor-pointer ${viewMode === "table" ? "bg-gold-500/20 text-gold-400" : "text-gray-400"}`}
            >
              <i className="ri-table-line" />
            </button>
            <button
              onClick={() => setViewMode("card")}
              className={`px-3 py-2 text-sm cursor-pointer ${viewMode === "card" ? "bg-gold-500/20 text-gold-400" : "text-gray-400"}`}
            >
              <i className="ri-layout-grid-line" />
            </button>
          </div>
        </div>
        <button onClick={() => setShowAddForm(true)} className="btn-gold">
          <i className="ri-add-line" />
          Yeni Müşteri
        </button>
      </div>

      {/* Table View */}
      {viewMode === "table" && (
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Ad Soyad</th>
                  <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Telefon</th>
                  <th className="text-left text-xs text-gray-500 font-medium px-4 py-3 hidden md:table-cell">E-posta</th>
                  <th className="text-left text-xs text-gray-500 font-medium px-4 py-3 hidden lg:table-cell">Adres</th>
                  <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">Alacak</th>
                  <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">Sipariş</th>
                  <th className="text-left text-xs text-gray-500 font-medium px-4 py-3 hidden sm:table-cell">Son İşlem</th>
                  <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id} className="table-row-hover border-b border-white/5 last:border-0">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gold-500 to-gold-700 flex items-center justify-center text-white text-xs font-semibold shrink-0">
                          {item.adSoyad.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                        </div>
                        <span className="text-sm text-gray-200 font-medium">{item.adSoyad}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">{item.telefon}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 hidden md:table-cell">
                      {item.eposta || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 hidden lg:table-cell max-w-[200px] truncate">
                      {item.adres || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium">
                      <span className={item.toplamAlacak > 0 ? "text-red-400" : "text-emerald-400"}>
                        {formatCurrency(item.toplamAlacak)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300 text-right">{item.toplamSiparis}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 hidden sm:table-cell">{item.sonIslem}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <a
                          href={`https://wa.me/${item.telefon.replace(/\s/g, "")}?text=Merhaba%20${encodeURIComponent(item.adSoyad)},%20size%20nasıl%20yardımcı%20olabiliriz?`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors cursor-pointer"
                          title="WhatsApp"
                        >
                          <i className="ri-whatsapp-line" />
                        </a>
                        <button
                          onClick={() => setEditingId(item.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 transition-colors cursor-pointer"
                          title="Düzenle"
                        >
                          <i className="ri-edit-line" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 text-gray-400 hover:bg-red-500/10 hover:text-red-400 transition-colors cursor-pointer"
                          title="Sil"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="p-8 text-center text-gray-500 text-sm">Müşteri bulunamadı.</div>
          )}
        </div>
      )}

      {/* Card View */}
      {viewMode === "card" && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((item) => (
            <div key={item.id} className="glass-card p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold-500 to-gold-700 flex items-center justify-center text-white text-sm font-semibold shrink-0">
                    {item.adSoyad.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-200">{item.adSoyad}</h4>
                    <p className="text-xs text-gray-500">{item.telefon}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <a
                    href={`https://wa.me/${item.telefon.replace(/\s/g, "")}?text=Merhaba%20${encodeURIComponent(item.adSoyad)},%20size%20nasıl%20yardımcı%20olabiliriz?`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors cursor-pointer"
                    title="WhatsApp"
                  >
                    <i className="ri-whatsapp-line text-xs" />
                  </a>
                  <button
                    onClick={() => setEditingId(item.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 transition-colors cursor-pointer"
                    title="Düzenle"
                  >
                    <i className="ri-edit-line text-xs" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/5 text-gray-400 hover:bg-red-500/10 hover:text-red-400 transition-colors cursor-pointer"
                    title="Sil"
                  >
                    <i className="ri-delete-bin-line text-xs" />
                  </button>
                </div>
              </div>
              <div className="space-y-2 mb-3">
                {item.eposta && (
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <i className="ri-mail-line" />
                    {item.eposta}
                  </div>
                )}
                {item.adres && (
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <i className="ri-map-pin-line" />
                    <span className="truncate">{item.adres}</span>
                  </div>
                )}
                {item.not && (
                  <div className="flex items-start gap-2 text-xs text-gray-500 bg-white/5 rounded-lg p-2">
                    <i className="ri-sticky-note-line shrink-0 mt-0.5" />
                    <span className="line-clamp-2">{item.not}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-white/5 text-xs">
                <div>
                  <span className="text-gray-500">Alacak:</span>{" "}
                  <span className={item.toplamAlacak > 0 ? "text-red-400 font-medium" : "text-emerald-400 font-medium"}>
                    {formatCurrency(item.toplamAlacak)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Sipariş:</span>{" "}
                  <span className="text-gray-200 font-medium">{item.toplamSiparis}</span>
                </div>
                <div>
                  <span className="text-gray-500">Son:</span>{" "}
                  <span className="text-gray-400">{item.sonIslem}</span>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full p-8 text-center text-gray-500 text-sm">Müşteri bulunamadı.</div>
          )}
        </div>
      )}

      {/* Add Modal */}
      {showAddForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="glass-card w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <h3 className="text-lg font-semibold text-gray-100">Yeni Müşteri Ekle</h3>
              <button onClick={() => setShowAddForm(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-200 cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleAdd} className="p-4 space-y-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Ad Soyad *</label>
                <input name="adSoyad" required className="input-dark w-full" placeholder="Müşteri adı soyadı" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Telefon *</label>
                <input name="telefon" required className="input-dark w-full" placeholder="+90 555 123 4567" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">E-posta</label>
                <input name="eposta" type="email" className="input-dark w-full" placeholder="ornek@email.com" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Adres</label>
                <textarea name="adres" rows={2} className="input-dark w-full resize-none" placeholder="Adres bilgisi..." />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Not</label>
                <textarea name="not" rows={2} className="input-dark w-full resize-none" placeholder="Müşteri hakkında not..." />
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowAddForm(false)} className="btn-dark">İptal</button>
                <button type="submit" className="btn-gold">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingId && editRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="glass-card w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <h3 className="text-lg font-semibold text-gray-100">Müşteri Düzenle</h3>
              <button onClick={() => setEditingId(null)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-200 cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleEdit} className="p-4 space-y-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Ad Soyad *</label>
                <input name="adSoyad" defaultValue={editRecord.adSoyad} required className="input-dark w-full" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Telefon *</label>
                <input name="telefon" defaultValue={editRecord.telefon} required className="input-dark w-full" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">E-posta</label>
                <input name="eposta" type="email" defaultValue={editRecord.eposta} className="input-dark w-full" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Adres</label>
                <textarea name="adres" rows={2} defaultValue={editRecord.adres} className="input-dark w-full resize-none" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Not</label>
                <textarea name="not" rows={2} defaultValue={editRecord.not} className="input-dark w-full resize-none" />
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={() => setEditingId(null)} className="btn-dark">İptal</button>
                <button type="submit" className="btn-gold">Güncelle</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}